<template>
    <div v-if="shortlist" class="shortlist-header">
        <VJoyWrapper align="center" justify="space-between" wrap="nowrap">
            <div class="shortlist-header__title">{{ t('profile.shortlist.title', [shortlist!.title]) }}</div>
            <div class="shortlist-header__items">
                <VJoyAvatarsList data-testid="profile-shortlist-list">
                    <VJoyWrapper justify="center" align="center" no-margin>
                        <div v-for="(item, index) in shortlist!.profiles" :key="item.accountId" class="shortlist-header__items-profile-selected">
                            <VJoyAvatar
                                :photo-url="photoUrl(item.photo)"
                                :data-testid="`profile-shortlist-redirect-profile-${index}`"
                                @click="redirectToProfile(item)"
                            ></VJoyAvatar>
                            <VJoyIcon
                                :data-testid="`profile-shortlist-remove-${index}`"
                                name="cross-circle"
                                @click="shortlistStore.remove(item.profileId)"
                            />
                        </div>
                        <VJoyAvatar v-for="index in remainingEmptyItems" :key="index" :photo-url="''"></VJoyAvatar>
                    </VJoyWrapper>
                </VJoyAvatarsList>
            </div>
            <ProjectProposal :display-subtitle="false" />
        </VJoyWrapper>
    </div>
</template>

<script setup lang="ts">
    import {navigateTo} from '@typed-router';
    import {computed, useGuestHost, onMounted, onUnmounted, storeToRefs, useAsyncData, useDevice, useTranslation} from '#imports';
    import {Photo} from '@malt/asset-utils';
    import {VJoyAvatar, VJoyAvatarsList, VJoyIcon, VJoyWrapper} from '@maltjoy/core-vue';
    import type {PhotoResponse, ShortlistProfileResponse} from '@api';
    import ProjectProposal from '~/features/profile-header/ui/ProjectProposal.vue';
    import {useShortlistStore} from '@/store/shortlist/ShortlistStore';

    const props = defineProps<{
        accountId?: string;
    }>();

    const maxItemsInShortlist = 6;

    // Composables
    const {isCrawler: isPageBrowsedByCrawler} = useDevice();
    const {t} = useTranslation();

    // Stores
    const shortlistStore = useShortlistStore();
    const {reload} = shortlistStore;
    const {shortlist} = storeToRefs(shortlistStore);

    // Computed values
    const shortlistLength = computed((): number => shortlist.value?.profiles.length || 0);
    const remainingEmptyItems = computed((): number => maxItemsInShortlist - shortlistLength.value);

    function photoUrl(picture: PhotoResponse): string {
        if (picture) {
            return new Photo(picture).getFaceThumbnailUrl({width: 125, height: 125, forcedFormat: 'webp'});
        }
        return '';
    }

    function redirectToProfile(shortlistedProfile: ShortlistProfileResponse) {
        if (props.accountId !== shortlistedProfile.accountId) {
            return navigateTo(`${useGuestHost()}${shortlistedProfile.url}`, {external: true});
        }
    }

    async function handleWindowFocus() {
        await reload();
    }

    if (isPageBrowsedByCrawler === false) {
        onMounted(() => {
            // Ensure we reload the shortlist when window become visible
            // Example: when opening multiple profiles in different tabs
            window.addEventListener('focus', handleWindowFocus);
        });

        onUnmounted(() => {
            window.removeEventListener('focus', handleWindowFocus);
        });
    }
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .shortlist-header {
        margin: auto;
        max-width: 1200px;
        padding: var(--joy-core-spacing-4) var(--joy-core-spacing-8);

        @include mq.screen_xs {
            margin-top: var(--joy-core-spacing-8);
        }

        &__title {
            font-family: var(--joy-font-family-title);
            font-weight: var(--joy-font-weight-normal);
            font-size: var(--joy-font-size-secondary-200);
            line-height: var(--joy-line-height-small);

            @include mq.screen_sm {
                display: none;
            }
        }

        &__items {
            &-profile-selected {
                position: relative;
                cursor: pointer;

                joy-icon {
                    position: absolute;
                    opacity: 0;
                    right: 0;
                    height: 16px;
                    cursor: pointer;
                    border-radius: 100%;
                    background-color: var(--joy-color-background-default);
                }

                &:hover {
                    joy-icon {
                        opacity: 1;
                    }
                }
            }
        }
    }

    joy-avatar {
        --avatar-placeholder-background: var(--joy-color-neutral-0) !important;

        border: 1px solid var(--joy-color-secondary-50) !important;
        border-radius: var(--joy-core-radius-6);
    }
</style>
